import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LANG_LIST, type LangCode } from '../../lib/i18n/languages';
import { setLanguage, currentLanguage } from '../../lib/i18n';
import { setPreference } from '../../lib/preferences';
import { useTheme } from '../../lib/theme';
import { useAuth } from '../../lib/auth';
import { haptic } from '../../lib/haptic';

/** Pick a time-of-day greeting key. Browser-local time is the correct signal
 *  here (same rule as HomeRoute — apps/api uses IST helpers, not this client). */
function greetingKey(now = new Date()): 'morning' | 'afternoon' | 'evening' {
  const h = now.getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function CheckIcon() {
  return (
    <svg
      width={12} height={12} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth={3}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function LanguageRoute() {
  const { t } = useTranslation('profile');
  const { t: tHome } = useTranslation('home');
  const nav = useNavigate();
  const current = currentLanguage();
  const themePref = useTheme(s => s.pref);
  const setThemePref = useTheme(s => s.setPref);
  const driver = useAuth(s => s.driver);

  // Live greeting — re-renders on each lang switch because `currentLanguage()`
  // changes after `setLanguage()` triggers an i18next event.
  const greeting = tHome(`greeting.${greetingKey()}`);
  const firstName = driver?.firstName || driver?.name || 'Rajesh';

  const onPickLang = (code: LangCode) => {
    if (code === current) return;
    haptic.select();
    setLanguage(code);
    setPreference('language', code);
    // Stay on the page so the user sees the live preview update.
  };

  return (
    <div className="px-5 pt-2 pb-10">
      <h1 className="text-2xl font-extrabold text-[var(--color-text)] mb-4">{t('language.title')}</h1>

      {/* Live greeting preview — re-renders when language switches. Name stays
          Latin Fraunces italic (cross-language editorial italic rule). */}
      <div
        className="mt-2 text-center"
        style={{
          padding: '22px 22px 24px',
          background: 'var(--mint-soft)',
          border: '1px solid var(--mint-line)',
          borderRadius: 'var(--radius-card)',
        }}
      >
        <div
          className="font-bold uppercase mb-2.5"
          style={{
            fontSize: 10,
            color: 'var(--green-700)',
            letterSpacing: '0.10em',
          }}
        >
          {t('language.previewLabel')}
        </div>
        <div
          className="font-extrabold leading-tight"
          style={{
            fontSize: 28,
            letterSpacing: '-0.02em',
            color: 'var(--color-text)',
          }}
        >
          {greeting}, <span className="serif-accent" style={{ fontSize: 30 }}>{firstName}</span>
        </div>
      </div>

      {/* Language section */}
      <h2
        className="text-[11px] font-bold uppercase mt-6 mb-3"
        style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}
      >
        {t('language.title')}
      </h2>
      <div role="radiogroup" aria-label={t('language.title')} className="grid grid-cols-2 gap-2.5">
        {LANG_LIST.map(lang => {
          const selected = current === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onPickLang(lang.code)}
              className="relative flex flex-col items-center text-center transition-all"
              style={{
                padding: '18px 14px 14px',
                background: selected ? 'var(--mint-soft)' : 'var(--color-surface)',
                border: `1.5px solid ${selected ? 'var(--color-primary-500,#2E9E65)' : 'var(--color-border)'}`,
                borderRadius: 16,
                boxShadow: '0 4px 14px rgba(15, 59, 38, 0.05)',
                gap: 4,
                minHeight: 'var(--touch-min, 44px)',
              }}
            >
              {selected ? (
                <span
                  className="absolute flex items-center justify-center"
                  style={{
                    top: 10, right: 10,
                    width: 22, height: 22,
                    borderRadius: 999,
                    background: 'var(--color-btn-primary,#2E9E65)',
                    color: 'white',
                  }}
                  aria-hidden
                >
                  <CheckIcon />
                </span>
              ) : null}
              <div
                className="font-bold leading-tight"
                style={{
                  fontSize: 26,
                  letterSpacing: '-0.02em',
                  color: selected ? 'var(--color-btn-primary)' : 'var(--color-text)',
                }}
              >
                {lang.native}
              </div>
              <div
                className="font-semibold"
                style={{
                  fontSize: 12,
                  color: selected ? 'var(--color-btn-primary)' : 'var(--color-text-muted,#6B7480)',
                  opacity: selected ? 0.85 : 1,
                }}
              >
                {lang.latin}
              </div>
              {!lang.full ? (
                <div
                  className="italic"
                  style={{
                    fontSize: 10,
                    color: 'var(--color-text-dim,#9AA3AC)',
                    marginTop: 2,
                  }}
                >
                  partial
                </div>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Theme picker */}
      <h2
        className="text-[11px] font-bold uppercase mt-6 mb-3"
        style={{ color: 'var(--color-text-dim,#9AA3AC)', letterSpacing: '0.08em' }}
      >
        {t('themePicker.title', { defaultValue: t('theme') })}
      </h2>
      <div role="radiogroup" aria-label={t('theme')} className="grid grid-cols-3 gap-2.5">
        {(['light', 'dark', 'system'] as const).map(mode => {
          const selected = themePref === mode;
          const label =
            mode === 'light'  ? t('themePicker.light',  { defaultValue: t('themeLight') }) :
            mode === 'dark'   ? t('themePicker.dark',   { defaultValue: t('themeDark') }) :
                                t('themePicker.system', { defaultValue: 'System' });
          // System preview shows a split light/dark card so the option reads as
          // "auto by device". theme.ts already resolves 'system' via
          // matchMedia('prefers-color-scheme: dark'); no other wiring needed here.
          return (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => { if (mode !== themePref) { haptic.tap(); setThemePref(mode); } }}
              className="relative flex flex-col transition-all"
              style={{
                padding: '12px 12px 14px',
                background: selected ? 'var(--mint-soft)' : 'var(--color-surface)',
                border: `1.5px solid ${selected ? 'var(--color-primary-500,#2E9E65)' : 'var(--color-border)'}`,
                borderRadius: 16,
                boxShadow: '0 4px 14px rgba(15, 59, 38, 0.05)',
                gap: 10,
                minHeight: 'var(--touch-min, 44px)',
              }}
            >
              {selected ? (
                <span
                  className="absolute flex items-center justify-center"
                  style={{
                    top: 8, right: 8,
                    width: 22, height: 22,
                    borderRadius: 999,
                    background: 'var(--color-btn-primary,#2E9E65)',
                    color: 'white',
                    zIndex: 2,
                  }}
                  aria-hidden
                >
                  <CheckIcon />
                </span>
              ) : null}
              {/* Mini-preview card — palette swatch. System mode shows a half-light / half-dark split. */}
              <div
                className="relative overflow-hidden"
                style={{
                  height: 78,
                  borderRadius: 10,
                  background:
                    mode === 'light'  ? '#FAFAF7' :
                    mode === 'dark'   ? '#14181E' :
                    'linear-gradient(90deg, #FAFAF7 50%, #14181E 50%)',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                }}
                aria-hidden
              >
                {/* title bar */}
                <div
                  style={{
                    position: 'absolute',
                    top: 8, left: 8, right: 8,
                    height: 8,
                    borderRadius: 4,
                    background:
                      mode === 'light'  ? 'rgba(0,0,0,0.10)' :
                      mode === 'dark'   ? 'rgba(255,255,255,0.16)' :
                      'linear-gradient(90deg, rgba(0,0,0,0.10) 50%, rgba(255,255,255,0.16) 50%)',
                  }}
                />
                {/* mini card */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 10, left: 8, right: 8,
                    height: 36,
                    borderRadius: 8,
                    background:
                      mode === 'light'  ? 'white' :
                      mode === 'dark'   ? '#1F262F' :
                      'linear-gradient(90deg, white 50%, #1F262F 50%)',
                    border: mode === 'light'
                      ? '1px solid rgba(0,0,0,0.08)'
                      : mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.12)'
                        : '1px solid rgba(0,0,0,0.06)',
                  }}
                />
                {/* mini text lines */}
                <div
                  style={{
                    position: 'absolute',
                    left: 14, right: 46, bottom: 36, height: 3,
                    borderRadius: 2,
                    background:
                      mode === 'light'  ? 'rgba(0,0,0,0.12)' :
                      mode === 'dark'   ? 'rgba(255,255,255,0.16)' :
                      'linear-gradient(90deg, rgba(0,0,0,0.12) 50%, rgba(255,255,255,0.16) 50%)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 14, right: 30, bottom: 28, height: 3,
                    borderRadius: 2,
                    background:
                      mode === 'light'  ? 'rgba(0,0,0,0.06)' :
                      mode === 'dark'   ? 'rgba(255,255,255,0.10)' :
                      'linear-gradient(90deg, rgba(0,0,0,0.06) 50%, rgba(255,255,255,0.10) 50%)',
                  }}
                />
                {/* green dot accent */}
                <div
                  style={{
                    position: 'absolute',
                    right: 14, bottom: 34,
                    width: 7, height: 7,
                    borderRadius: 999,
                    background: 'var(--color-btn-primary,#2E9E65)',
                  }}
                />
              </div>
              <div
                className="text-center font-bold"
                style={{
                  fontSize: 13,
                  letterSpacing: '-0.01em',
                  color: selected ? 'var(--color-btn-primary)' : 'var(--color-text)',
                }}
              >
                {label}
              </div>
            </button>
          );
        })}
      </div>

      {/* Back button — preserves prior nav UX */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => { haptic.tap(); nav(-1); }}
          className="text-sm font-semibold text-[var(--color-text-secondary)] underline-offset-2 hover:underline"
        >
          {t('back', { ns: 'common', defaultValue: 'Back' })}
        </button>
      </div>
    </div>
  );
}
